"use client";

import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/shadcn-components/ui/dialog";
import { useAccountInformationStore } from "@/vybe-trader-client/account-information/stores/useAccountInformationStore";
import { useSubscribedSymbolsStore } from "@/vybe-trader-client/subscribed-symbols/stores/useSubscribedSymbolsStore";
import { useSymbolsStore } from "@/vybe-trader-client/symbols/stores/useSymbolsStore";
import {
  formatBrokerDateTime,
  formatDaySessions,
  getDayLabel,
  isQuoteSessionActive,
  isTradeSessionActive,
} from "@/vybe-trader-client/symbols/symbolSessions";
import {
  formatConfigSpreadNote,
  formatMarginFlags,
  formatMarginRateRows,
  formatOrderFlags,
  formatProfitCalcFlags,
  formatVolumeInternal,
  formatSwapRateRows,
  getCommissionSectionRows,
  getGroupTemplateRows,
  getSymbolBaseInfoRows,
  type SymbolDetailItem,
} from "@/vybe-trader-client/symbols/symbolDisplayFormatters";
import {
  formatCalculationMode,
  formatExecutionType,
  formatExpirationFlags,
  formatFillingFlags,
  formatGTCMode,
  formatSwapType,
  formatTradeMode,
  formatV3DaysSwap,
  type Symbol as VybeSymbol,
} from "@/vybe-trader-client/symbols/types";

interface SymbolDetailsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currency: string | null;
}

const SESSION_TICK_MS = 30_000;
const DISPLAY_DAY_ORDER = [1, 2, 3, 4, 5, 6, 0];

function DetailRow({ label, value }: SymbolDetailItem) {
  return (
    <div className="flex flex-col gap-0.5 py-1.5 border-b border-border/70 last:border-0">
      <span className="text-xs text-muted-foreground shrink-0">{label}</span>
      <span className="text-sm font-medium text-foreground break-all">
        {value}
      </span>
    </div>
  );
}

function DetailSection({
  title,
  rows,
  note,
}: {
  title: string;
  rows: SymbolDetailItem[];
  note?: string;
}) {
  return (
    <section className="mb-4 last:mb-0">
      <p className="text-xs font-medium text-muted-foreground mb-2">
        {title}
      </p>
      {note ? (
        <p className="text-xs text-muted-foreground mb-2">{note}</p>
      ) : null}
      <div className="rounded-md border border-border/70 px-3">
        {rows.map((row) => (
          <DetailRow key={`${title}-${row.label}`} {...row} />
        ))}
      </div>
    </section>
  );
}

const emptyValue = "—";

const formatText = (value: string | undefined | null) =>
  value && value.trim() ? value : emptyValue;

const formatNum = (n: number | undefined, d?: number) =>
  n !== undefined && Number.isFinite(n) ? n.toFixed(d) : emptyValue;

const formatInt = (n: number | undefined) =>
  n !== undefined && Number.isFinite(n) ? String(n) : emptyValue;

const formatTime = (value: number | undefined) => {
  if (value === undefined || !Number.isFinite(value) || value <= 0) {
    return emptyValue;
  }

  const millis = Math.abs(value) < 1_000_000_000_000 ? value * 1000 : value;
  const date = new Date(millis);

  return Number.isNaN(date.getTime())
    ? String(value)
    : `${value} - ${date.toLocaleString()}`;
};

const formatColor = (value: number | undefined) => {
  if (value === undefined || !Number.isFinite(value)) return emptyValue;

  return `${value} - #${(value >>> 0).toString(16).padStart(8, "0")}`;
};

const formatBool = (value: boolean) => (value ? "Yes" : "No");

function getSessionScheduleRows(symbol: VybeSymbol): SymbolDetailItem[] {
  if (!symbol.sessions) {
    return [{ label: "Schedule", value: emptyValue }];
  }

  return DISPLAY_DAY_ORDER.map((dayIndex) => ({
    label: getDayLabel(dayIndex),
    value: `Quote: ${formatDaySessions(symbol.sessions?.quoteSessions?.[dayIndex])} | Trade: ${formatDaySessions(symbol.sessions?.tradeSessions?.[dayIndex])}`,
  }));
}

function getSymbolSections(symbol: VybeSymbol): Array<{
  title: string;
  rows: SymbolDetailItem[];
  note?: string;
}> {
  const digits = symbol.digits ?? 5;

  const marginRows: SymbolDetailItem[] = [
    { label: "Initial margin", value: formatNum(symbol.initialMargin, 6) },
    {
      label: "Maintenance margin",
      value: formatNum(symbol.maintenanceMargin, 6),
    },
    { label: "Hedged margin", value: formatNum(symbol.hedgedMargin, 6) },
    ...formatMarginRateRows(symbol.initMarginRate, "Initial margin rate"),
    ...formatMarginRateRows(symbol.mntnMarginRate, "Maintenance margin rate"),
    { label: "Margin flags", value: formatMarginFlags(symbol.marginFlags) },
    {
      label: "Collateral liquidity rate",
      value: formatNum(symbol.collateralLiquidityRate, 6),
    },
    {
      label: "Hedged margin rate",
      value: formatNum(symbol.hedgedMarginRate, 6),
    },
    { label: "Margin precision", value: formatInt(symbol.marginPrecision) },
    {
      label: "Profit calc flags",
      value: formatProfitCalcFlags(symbol.profitCalcFlags),
    },
  ];

  const sections: Array<{ title: string; rows: SymbolDetailItem[]; note?: string }> = [
    {
      title: "Instrument",
      rows: [
        { label: "Symbol", value: formatText(symbol.currency) },
        { label: "Symbol currency", value: formatText(symbol.symbolCurrency) },
        { label: "Description", value: formatText(symbol.description) },
        { label: "Basis", value: formatText(symbol.basis) },
        { label: "ISIN", value: formatText(symbol.isin) },
        { label: "Reference site", value: formatText(symbol.refToSite) },
        { label: "Profit currency", value: formatText(symbol.profitCurrency) },
        { label: "Margin currency", value: formatText(symbol.marginCurrency) },
        { label: "Group", value: formatText(symbol.groupName) },
        { label: "Id", value: formatInt(symbol.id) },
        { label: "Custom", value: formatInt(symbol.custom) },
        { label: "Background color", value: formatColor(symbol.bkgndColor) },
      ],
    },
    {
      title: "Pricing",
      rows: [
        { label: "Digits", value: formatInt(symbol.digits) },
        { label: "Precision", value: formatInt(symbol.precision) },
        { label: "Points", value: formatNum(symbol.points, digits) },
        { label: "Limit points", value: formatNum(symbol.limitPoints, digits) },
        { label: "Contract size", value: formatNum(symbol.contractSize, 2) },
        { label: "Tick value", value: formatNum(symbol.tickValue, digits) },
        { label: "Bid tick value", value: formatNum(symbol.bidTickValue, digits) },
        { label: "Ask tick value", value: formatNum(symbol.askTickValue, digits) },
        { label: "Tick size", value: formatNum(symbol.tickSize, digits) },
        {
          label: "Spread (config points)",
          value: formatInt(symbol.spread),
        },
        { label: "Settlement price", value: formatNum(symbol.settlementPrice, digits) },
        { label: "Lower limit", value: formatNum(symbol.lowerLimit, digits) },
        { label: "Upper limit", value: formatNum(symbol.upperLimit, digits) },
        { label: "Face value", value: formatNum(symbol.faceValue, 2) },
        { label: "Accrued interest", value: formatNum(symbol.accruedInterest, 6) },
      ],
    },
    {
      title: "Trading Rules",
      rows: [
        { label: "Calculation mode", value: formatCalculationMode(symbol.calcMode) },
        { label: "GTC mode", value: formatGTCMode(symbol.gtcMode) },
        { label: "Trade mode", value: formatTradeMode(symbol.tradeMode) },
        { label: "Execution type", value: formatExecutionType(symbol.tradeType) },
        { label: "Filling flags", value: formatFillingFlags(symbol.fillingFlags) },
        { label: "Expiration flags", value: formatExpirationFlags(symbol.expirationType) },
        { label: "Order flags", value: formatOrderFlags(symbol.orderFlags) },
        { label: "SL level", value: formatInt(symbol.sl) },
        { label: "TP level", value: formatInt(symbol.tp) },
        { label: "Deviation rate", value: formatInt(symbol.deviationRate) },
        { label: "Round rate", value: formatInt(symbol.roundRate) },
        { label: "Price timeout", value: formatInt(symbol.priceTimeout) },
        { label: "Requote timeout", value: formatInt(symbol.requoteTimeout) },
        { label: "Depth of market", value: formatInt(symbol.depthOfMarket) },
      ],
    },
    {
      title: "Volume",
      rows: [
        { label: "Request lots", value: formatInt(symbol.requestLots) },
        { label: "Min lots", value: formatNum(symbol.minLots, 2) },
        { label: "Min volume", value: formatVolumeInternal(symbol.minVolume) },
        { label: "Max lots", value: formatNum(symbol.maxLots, 2) },
        { label: "Max volume", value: formatVolumeInternal(symbol.maxVolume) },
        { label: "Lots step", value: formatNum(symbol.lotsStep, 2) },
        { label: "Volume step", value: formatVolumeInternal(symbol.volumeStep) },
      ],
    },
    {
      title: "Margin",
      rows: marginRows,
    },
    {
      title: "Swap",
      rows: [
        { label: "Swap type", value: formatSwapType(symbol.swapType) },
        { label: "Swap long", value: formatNum(symbol.swapLong, 6) },
        { label: "Swap short", value: formatNum(symbol.swapShort, 6) },
        { label: "Triple swap day", value: formatV3DaysSwap(symbol.v3DaysSwap) },
        ...formatSwapRateRows(symbol.swapRates),
      ],
    },
    {
      title: "Metadata Timestamps",
      rows: [
        { label: "Update time", value: formatTime(symbol.updateTime) },
        { label: "First trade time", value: formatTime(symbol.firstTradeTime) },
        { label: "Last trade time", value: formatTime(symbol.lastTradeTime) },
      ],
    },
    {
      title: "Session Schedule",
      note: "Quote and trade windows use broker server time (Mon–Sun).",
      rows: getSessionScheduleRows(symbol),
    },
    {
      title: "Commission",
      rows: getCommissionSectionRows(symbol.commission),
    },
  ];

  return sections;
}

export default function SymbolDetailsDialog({
  open,
  onOpenChange,
  currency,
}: SymbolDetailsDialogProps) {
  const symbol = useSymbolsStore((s) =>
    open && currency ? s.getSymbolByCurrency(currency) : undefined
  );
  const baseInfo = useSymbolsStore((s) => s.baseInfo);
  const groupTemplates = useSymbolsStore((s) => s.groupTemplates);
  const serverTimeZoneMinutes = useAccountInformationStore(
    (s) => s.account?.serverTimeZoneMinutes ?? 0
  );
  const hasAccountTimezone = useAccountInformationStore((s) => s.account != null);
  const entry = useSubscribedSymbolsStore((s) =>
    open && currency ? s.byCurrency.get(currency) : undefined
  );
  const quote = entry?.quote;
  const digits = symbol?.digits ?? entry?.symbol.digits ?? 5;

  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    if (!open) return;

    setNowMs(Date.now());
    const timer = window.setInterval(() => setNowMs(Date.now()), SESSION_TICK_MS);
    return () => window.clearInterval(timer);
  }, [open, currency]);

  const sessionStatusRows = useMemo((): SymbolDetailItem[] => {
    if (!symbol?.sessions) {
      return [{ label: "Status", value: emptyValue }];
    }

    const quoteActive = isQuoteSessionActive(
      symbol.sessions,
      serverTimeZoneMinutes,
      nowMs
    );
    const tradeActive = isTradeSessionActive(
      symbol.sessions,
      serverTimeZoneMinutes,
      nowMs
    );

    return [
      { label: "Quote session active", value: formatBool(quoteActive) },
      { label: "Trade session active", value: formatBool(tradeActive) },
      {
        label: "Broker time (UTC offset applied)",
        value: formatBrokerDateTime(serverTimeZoneMinutes, nowMs),
      },
    ];
  }, [symbol?.sessions, serverTimeZoneMinutes, nowMs]);

  const matchedTemplates = useMemo(() => {
    if (!symbol) return [];

    const matchedSet = new Set(symbol.matchedGroupTemplates ?? []);
    return groupTemplates.filter((template) =>
      matchedSet.has(template.groupName)
    );
  }, [symbol, groupTemplates]);

  const spreadNote = formatConfigSpreadNote(symbol?.spread, quote?.spread);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] p-4 flex flex-col gap-0">
        <DialogHeader className="pb-2 shrink-0">
          <DialogTitle className="text-lg">
            {currency ?? "Symbol"} details
          </DialogTitle>
        </DialogHeader>
        <div className="overflow-y-auto flex-1 min-h-0 pr-1">
          {!symbol && !quote && (
            <p className="text-sm text-muted-foreground">
              No symbol metadata or quote loaded for this instrument yet.
            </p>
          )}
          {symbol && (
            <>
              <DetailSection
                title="Live Session Status"
                note={
                  hasAccountTimezone
                    ? "Computed on the client from session schedule and broker timezone."
                    : "Broker timezone not loaded yet; using UTC offset 0."
                }
                rows={sessionStatusRows}
              />
              {getSymbolSections(symbol).map((section) => (
                <DetailSection key={section.title} {...section} />
              ))}
              <DetailSection
                title="Group Templates"
                note="Wildcard group rules merged into this symbol's effective configuration."
                rows={[
                  { label: "Effective group", value: formatText(symbol.groupName) },
                  {
                    label: "Matched templates",
                    value: symbol.matchedGroupTemplates?.length
                      ? symbol.matchedGroupTemplates.join(", ")
                      : emptyValue,
                  },
                ]}
              />
              {matchedTemplates.map((template) => (
                <DetailSection
                  key={template.groupName}
                  title={`Template: ${template.groupName}`}
                  rows={getGroupTemplateRows(template)}
                />
              ))}
              {baseInfo && (
                <DetailSection
                  title="Symbol Base Info"
                  note="Account-wide symbol configuration from the broker connection."
                  rows={getSymbolBaseInfoRows(baseInfo)}
                />
              )}
            </>
          )}

          {!quote && (symbol || currency) && (
            <p className="text-sm text-muted-foreground">No live quote yet.</p>
          )}
          {quote && (
            <DetailSection
              title="Latest quote"
              note={spreadNote}
              rows={[
                { label: "Bid", value: formatNum(quote.bid, digits) },
                { label: "Ask", value: formatNum(quote.ask, digits) },
                { label: "Last", value: formatNum(quote.last, digits) },
                {
                  label: "Last price (prev.)",
                  value: formatNum(quote.lastPrice, digits),
                },
                { label: "Volume", value: formatNum(quote.volume, 2) },
                { label: "High", value: formatNum(quote.high, digits) },
                { label: "Low", value: formatNum(quote.low, digits) },
                { label: "Open price", value: formatNum(quote.openPrice, digits) },
                { label: "Close price", value: formatNum(quote.closePrice, digits) },
                { label: "Daily change", value: formatNum(quote.dailyChange, 2) },
                { label: "Spread (live)", value: formatInt(quote.spread) },
                {
                  label: "Market watch volume",
                  value: formatInt(quote.marketWatchVolume),
                },
                {
                  label: "Quote time",
                  value: quote.time
                    ? new Date(quote.time).toLocaleString()
                    : emptyValue,
                },
              ]}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
